"use client";
import React from "react";
import { FaWhatsapp } from "react-icons/fa6";
import {
  Bed,
  Binoculars,
  CarFront,
  Coffee,
  MapPin,
  Phone,
  Clock,
  Tags,
} from "lucide-react";
import Link from "next/link";
import { motion, Variants } from "framer-motion";
import { IPackageDetailsData } from "../interface/IPackageDetails.interface";
import { useSession } from "next-auth/react";
import { useModal } from "@/core/context/ModalContext";
import { showErrorMessage } from "@/utils/toastMessage/toast.message";
import { IOrganizationData } from "@/app/contact-us/interface/IOrganizationInterface";

interface IProps {
  packageData: IPackageDetailsData;
  orgData?: IOrganizationData;
}

const packageDetail = [
  { id: 1, icon: Bed, label: "Hotel" },
  { id: 2, icon: Binoculars, label: "Sights" },
  { id: 3, icon: CarFront, label: "Transfer" },
  { id: 4, icon: Coffee, label: "Meals" },
];

const containerVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const BookNowSection: React.FC<IProps> = ({ packageData, orgData }) => {
  const { status } = useSession();
  const isLoggedIn = status === "authenticated";
  const { openModal } = useModal();
  console.log("org", orgData);
  const handleNotLoggedIn = () => {
    openModal("login");
    showErrorMessage("Please login to access this feature.");
  };

  return (
    <motion.div
      className="max-w-5xl mx-auto px-4 lg:px-0 -mt-28 relative z-10"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="bg-white rounded-xl border border-gray-200 shadow-xl flex flex-col lg:flex-row items-stretch overflow-hidden">
        <div className="flex-1.5 p-5 lg:p-6 flex flex-col justify-center">
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <motion.div
              variants={itemVariants}
              className="flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded-xl text-xs font-bold uppercase"
            >
              <MapPin size={12} /> {packageData?.destination}
            </motion.div>
            <motion.div
              variants={itemVariants}
              className="flex items-center gap-1 px-2 py-0.5 bg-secondary-50 text-secondary-700 rounded-xl text-xs font-black uppercase"
            >
              <Tags size={12} /> {packageData?.category?.name}
            </motion.div>
            <motion.div
              variants={itemVariants}
              className="flex items-center gap-1 px-2 py-0.5 bg-tertiary-50 text-gray-600 rounded-xl text-xs font-bold uppercase"
            >
              <Clock size={12} /> {packageData?.duration} Days
            </motion.div>
          </div>

          <div className="flex items-center gap-6">
            {packageDetail.map((item) => (
              <motion.div
                key={item.id}
                variants={itemVariants}
                className="flex items-center gap-3 opacity-80 hover:opacity-100 transition-opacity group cursor-pointer"
              >
                <div className="flex items-center justify-center w-9 h-9 rounded-full border border-gray-900 bg-gray-50 group-hover:border-primary-500 group-hover:bg-primary-50 transition-colors">
                  <item.icon
                    size={20}
                    className="text-gray-700 group-hover:text-primary-600"
                    strokeWidth={2}
                  />
                </div>
                <span className="font-bold text-gray-900 uppercase text-[11px] tracking-tight">
                  {item.label}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          className="flex-1 bg-gray-50/50 border-t lg:border-t-0 lg:border-l border-gray-100 p-5 lg:px-8 lg:py-4 flex items-center justify-between lg:justify-end gap-8"
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <div className="text-right">
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest leading-none mb-1">
              Price Starts At
            </p>
            <div className="flex items-center gap-2 justify-end">
              <div className="flex flex-col items-end gap-1">
                <span className="text-2xl font-black text-gray-900 leading-none">
                  <span className="text-xs text-primary-600 mr-1">NRP</span>
                  {packageData?.current_price?.toLocaleString()}
                </span>
                <span className="text-xs text-gray-400 line-through">
                  NRP {packageData?.previous_price?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isLoggedIn ? (
              <Link href="/user/book/1">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-xl transition-all font-bold uppercase tracking-widest text-[11px] flex items-center gap-2 shadow-lg shadow-primary-500/20"
                >
                  Book Now
                </motion.button>
              </Link>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNotLoggedIn}
                className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-xl transition-all font-bold uppercase tracking-widest text-[11px] shadow-lg shadow-primary-500/20"
              >
                Book Now
              </motion.button>
            )}
            <motion.a
              href={`tel:${orgData?.phone}`}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -2 }}
              className="p-3 bg-white border border-gray-200 rounded-xl text-gray-400 hover:text-primary-600 transition-all shadow-sm inline-flex items-center justify-center"
            >
              <Phone size={16} />
            </motion.a>

            <a
              href={`https://wa.me/${orgData?.phone}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-[#25D366] text-white rounded-xl hover:bg-[#20ba5a] transition-all inline-flex items-center justify-center"
            >
              <FaWhatsapp size={18} />
            </a>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default BookNowSection;
